import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useRef,
} from "react";
import { EnhancedTokenManager } from "../utils/tokenManager";
import { BASE_URL } from "../api/urls";
import AvatarIcon from "../assets/images/profile-avatar.svg";

interface ProfileImageContextType {
  profileImage: string;
  loading: boolean;
  refreshProfileImage: () => Promise<void>;
}

const ProfileImageContext = createContext<ProfileImageContextType | undefined>(undefined);

export const ProfileImageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [profileImage, setProfileImage] = useState<string>(AvatarIcon);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const hasExternalImageRef = useRef(false);

  const fetchProfileImage = async (forceRefresh = false) => {
    try {
      if (hasExternalImageRef.current && !forceRefresh) return;
      setLoading(true);
      const accessToken = EnhancedTokenManager.getAccessToken();

      if (!accessToken) {
        setProfileImage(AvatarIcon);
        hasExternalImageRef.current = false;
        setLoading(false);
        return;
      }

      let userProfile = EnhancedTokenManager.getUserProfile();

      // Profile not cached (e.g. after cross-subdomain redirect) — fetch it once
      if (!userProfile) {
        const result = await EnhancedTokenManager.fetchAndCheckUserProfile(accessToken);
        if (result.success) {
          userProfile = EnhancedTokenManager.getUserProfile();
        }
      }

      if (!userProfile?.profile_image) {
        setProfileImage(AvatarIcon);
        hasExternalImageRef.current = false;
        setLoading(false);
        return;
      }

      const imageUrl = userProfile.profile_image.startsWith("http")
        ? userProfile.profile_image
        : `${BASE_URL}${userProfile.profile_image.startsWith("/") ? "" : "/"}${userProfile.profile_image}`;

      const isExternalImage = imageUrl.startsWith("http") && !imageUrl.includes(BASE_URL);

      if (isExternalImage) {
        if (profileImage !== AvatarIcon && profileImage.startsWith('blob:')) {
          URL.revokeObjectURL(profileImage);
        }
        setProfileImage(prevImage => prevImage === imageUrl ? imageUrl + '?_=' + Date.now() : imageUrl);
        hasExternalImageRef.current = true;
        setLoading(false);
        return;
      }

      hasExternalImageRef.current = false;
      const imageResponse = await fetch(`${imageUrl}?t=${Date.now()}`, {
        method: "GET",
        headers: { Authorization: `Bearer ${accessToken}` },
        cache: "no-store",
      });

      if (imageResponse.ok) {
        const imageBlob = await imageResponse.blob();
        if (profileImage !== AvatarIcon && profileImage.startsWith("blob:")) {
          URL.revokeObjectURL(profileImage);
        }
        setProfileImage(URL.createObjectURL(imageBlob));
      } else {
        setProfileImage(AvatarIcon);
      }
    } catch (error) {
      setProfileImage(AvatarIcon);
      hasExternalImageRef.current = false;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const checkAuth = () => {
      const hasToken = !!EnhancedTokenManager.getAccessToken();
      if (hasToken && !isAuthenticated) {
        setIsAuthenticated(true);
        fetchProfileImage(); // fetches profile from API if not cached
      } else if (!hasToken && isAuthenticated) {
        setIsAuthenticated(false);
        setProfileImage(AvatarIcon);
        hasExternalImageRef.current = false;
      }
    };
    checkAuth();
    const interval = setInterval(checkAuth, 2000);
    const timeout = setTimeout(() => clearInterval(interval), 10000);
    return () => { clearInterval(interval); clearTimeout(timeout); };
  }, [isAuthenticated]);

  useEffect(() => {
    const accessToken = EnhancedTokenManager.getAccessToken();
    if (accessToken) {
      setIsAuthenticated(true);
      fetchProfileImage(); // fetches profile from API if not cached
    } else {
      setLoading(false);
    }

    const handleProfileUpdate = (event: Event) => {
      const customEvent = event as CustomEvent<{ immediateImageUrl?: string }>;
      const immediateUrl = customEvent?.detail?.immediateImageUrl;
      if (immediateUrl) {
        const isExternal = immediateUrl.startsWith("http") && !immediateUrl.includes(BASE_URL);
        if (profileImage !== AvatarIcon && profileImage.startsWith("blob:")) URL.revokeObjectURL(profileImage);
        setProfileImage(immediateUrl);
        hasExternalImageRef.current = isExternal;
        setLoading(false);
      } else {
        fetchProfileImage(true);
      }
    };

    const handleAuthUpdate = () => {
      if (!!EnhancedTokenManager.getAccessToken()) {
        setIsAuthenticated(true);
        fetchProfileImage(true);
      }
    };

    window.addEventListener("profile-image-updated", handleProfileUpdate);
    window.addEventListener("auth-state-changed", handleAuthUpdate);
    window.addEventListener("user-profile-updated", handleAuthUpdate);
    return () => {
      window.removeEventListener("profile-image-updated", handleProfileUpdate);
      window.removeEventListener("auth-state-changed", handleAuthUpdate);
      window.removeEventListener("user-profile-updated", handleAuthUpdate);
      if (profileImage !== AvatarIcon && profileImage.startsWith("blob:")) URL.revokeObjectURL(profileImage);
    };
  }, []);

  const refreshProfileImage = async () => { await fetchProfileImage(true); };

  return (
    <ProfileImageContext.Provider value={{ profileImage, loading, refreshProfileImage }}>
      {children}
    </ProfileImageContext.Provider>
  );
};

export const useProfileImage = () => {
  const context = useContext(ProfileImageContext);
  if (context === undefined) throw new Error("useProfileImage must be used within ProfileImageProvider");
  return context;
};

export const triggerProfileImageUpdate = (immediateImageUrl?: string) => {
  window.dispatchEvent(new CustomEvent("profile-image-updated", { detail: { immediateImageUrl } }));
};

export const notifyAuthStateChanged = () => {
  window.dispatchEvent(new CustomEvent("auth-state-changed"));
};
